import { Point, Rectangle, Vector } from "../../../../domain/drawing";
import { DragKinds } from "../../DragKinds";
import { ITransformable } from "../ITransformable";
import { AbstractTransformer } from "./AbstractTransformer";

export class AspectRatioFixedTransformer extends AbstractTransformer {
  constructor(private readonly _transformer: ITransformable) {
    super();
  }

  transformRectangle(source: Rectangle, pointer: Point, kind: DragKinds): Rectangle {
    const aspectRatio = (source.width > 0 && source.height > 0)
      ? source.width / source.height
      : 0;
    const transformed = this._transformer.transformRectangle(source, pointer, kind);
    if (this.isResizingVertical(kind)) {
      this.adjustWidth(transformed, aspectRatio, this.isResizingToLeft(kind));
    }
    if (this.isResizingHorizontal(kind)) {
      this.adjustHeight(transformed, aspectRatio, this.isResizingToTop(kind));
    }
    return transformed;
  }

  transformVector(source: Vector, pointer: Point, kind: DragKinds): Vector {
    const transformed = this._transformer.transformVector(source, pointer, kind);
    if ((transformed.from.x !== source.from.x) || (transformed.from.y !== source.from.y)) {
      transformed.from = this.adjustFrom(source, transformed);
    } else {
      transformed.to = this.adjustTo(source, transformed);
    }
    return transformed;
  }

  private adjustFrom(source: Vector, transformed: Vector): Point {
    return this.adjustPoint(source, transformed, true);
  }

  private adjustTo(source: Vector, transformed: Vector): Point {
    return this.adjustPoint(source, transformed, false);
  }

  private adjustPoint(source: Vector, transformed: Vector, isFrom: boolean = false): Point {
    const aspectRatio = this.toAspectRatio(source);
    const transformedAspectRatio = this.toAspectRatio(transformed);
    const toTarget = (s: Vector) => isFrom ? s.from : s.to;
    const toOther = (s: Vector) => !isFrom ? s.from : s.to;
    const sourceToTarget = toTarget(source);
    const sourceToOther = toOther(source);
    const transformedToTarget = toTarget(transformed);
    const transformedToOther = toOther(transformed);
    return (aspectRatio < transformedAspectRatio)
      ? (sourceToTarget.y - sourceToOther.y) !== 0
        ? new Point(sourceToOther.x + (sourceToTarget.x - sourceToOther.x) * (transformedToTarget.y - transformedToOther.y) / (sourceToTarget.y - sourceToOther.y), transformedToTarget.y)
        : new Point(transformedToTarget.x, sourceToTarget.y)
      : (sourceToTarget.x - sourceToOther.x) !== 0
        ? new Point(transformedToTarget.x, sourceToOther.y + (sourceToTarget.y - sourceToOther.y) * (transformedToTarget.x - transformedToOther.x) / (sourceToTarget.x - sourceToOther.x))
        : new Point(sourceToTarget.x, transformedToTarget.y);
  }

  private toAspectRatio(source: Vector): number {
    const rectangle = Rectangle.from(source);
    return (rectangle.width > 1 ? rectangle.width : 1) / (rectangle.height > 1 ? rectangle.height : 1);
  }

  private adjustWidth(rect: Rectangle, ratio: number, isLeftMove: boolean) {
    if (ratio > 0) {
      // 縦横比を維持したまま変形させる
      const width = rect.size.height * ratio;
      if (isLeftMove) {
        rect.topLeft.x = rect.topLeft.x + rect.size.width - width;
      }
      rect.size.width = width > 1
        ? width
        : 1;
    }
  }

  private adjustHeight(rect: Rectangle, ratio: number, isTopMove: boolean) {
    if (ratio > 0) {
      // 縦横比を維持したまま変形させる
      const height = rect.size.width / ratio;
      if (isTopMove) {
        rect.topLeft.y = rect.topLeft.y + rect.size.height - height;
      }
      rect.size.height = height > 1
        ? height
        : 1;
    }
  }
}
